import { useTranslation } from 'next-i18next';
import { useLocale } from '@hooks/useLocale';
import cn from 'classnames';
import Heading from '@components/ui/Heading';
import IconWrapper from '@components/ui/Icons';
import StoreAvailability from '@components/product/StoreAvailability';
import { weeklyHours } from '@utils/store';
import { StoreHour, Store } from '@adapters/prismic/fetching/stores/types';
import Link from 'next/link';
import { useStoresUI } from '@context/StoresContext';
import { useUI } from '@context/GlobalUIContext';
import { getTodaysHours } from './getTodayHours';
import { buildSPUHoursMessaging } from '@utils/store/buildSPUHoursMessaging';
import { trackSelectStoreInteraction } from '@utils/gtm/ga4/trackStoreInteraction';

import styles from './StoreCard.module.scss';
import { useStoreAvailability } from '@utils/store/useStoreAvailability';

export type StoreCardT = (
  key: string,
  values?: Record<string, string | number | Date | undefined>
) => string;

export interface StoreCardProps {
  storesData: Store;
  path?: string | null;
  isModal?: boolean;
  isPdp?: boolean;
  filter?: string | null;
  /** When provided (e.g. from app router with next-intl), used instead of next-i18next. Keys use namespace:key format. */
  t?: StoreCardT;
}

const StoreCard = ({
  storesData,
  path,
  isModal = false,
  isPdp = false,
  filter = null,
  t: tProp,
}: StoreCardProps) => {
  const { t: tI18next } = useTranslation(['stores', 'common', 'pdp']);
  const t = tProp ?? tI18next;
  const locale = useLocale();
  const { displayStockLevelModal, closeStockLevelModal } = useUI();

  const { data } = storesData;
  const {
    store_name: storeName,
    address_line_1: addressStreet,
    address_city: addressCity,
    address_province: addressProvince,
    address_postalcode: addressPostalCode,
    phone,
    store_hours: storeHours,
    store_hour_exceptions: storeHourExceptions,
    drive_directions: driveDirections,
    mec_store_id: storeId,
  } = data;

  const {
    setFavouriteStore,
    favouriteStoreDetails,
    setLocationAndFavouriteStore,
  } = useStoresUI();

  const handleSelectMyStore = () => {
    trackSelectStoreInteraction({
      data: {
        interactionLocation: isPdp ? 'PDP' : 'store selector',
        storeName,
      },
    });
    if (storeId) {
      setFavouriteStore(storeId);
      setLocationAndFavouriteStore(data.geo);
      if (isModal && displayStockLevelModal) {
        closeStockLevelModal();
      }
    }
  };

  const orderedWeek: StoreHour[] = weeklyHours(storeHours, storeHourExceptions);
  const today = orderedWeek[0];

  const todaysHours = getTodaysHours(today, t);

  const spuMessageObj = buildSPUHoursMessaging(storesData, t);
  const pickupMessage = t(`pdp:product.pickup.short.${spuMessageObj.key}`, {
    day: spuMessageObj.day ?? undefined,
  });

  const isFavouriteStore =
    favouriteStoreDetails?.data?.mec_store_id === storesData?.data.mec_store_id;

  const result = useStoreAvailability(
    storeId,
    pickupMessage ?? '',
    null,
    isModal
  );

  if (result?.availabilityMessage === 'Pick up next day') {
    // SPU Store, check if we are doing STS and skip this if we are
    if (filter && filter === 'sts') {
      return null;
    }
  }

  if (result?.availabilityMessage?.includes('Pick up in-store in') ?? false) {
    // STS Store, check if we are doing SPU and skip this if we are
    if (filter && filter === 'spu') {
      return null;
    }
  }

  return (
    <address
      className={cn(styles.store, {
        [styles.pdpStore]: isModal,
        [styles.isFavourite]:
          favouriteStoreDetails?.data?.mec_store_id ===
          storesData?.data.mec_store_id,
      })}
    >
      <div
        className={cn(styles.title, styles.split, {
          [styles.pdpSplit]: isModal,
        })}
      >
        {isModal ? (
          <Heading semanticLevel="h3" styleLevel="5">
            {storeName}
          </Heading>
        ) : (
          <Link href={`/${locale}/stores/${path}`} className={styles.name}>
            {storeName}
            <IconWrapper
              icon="caret-right"
              height={14}
              classNames={styles.icon}
            />
          </Link>
        )}
        {isFavouriteStore ? (
          <button
            type="button"
            className={cn(styles.button, {
              [styles.isActive]: isFavouriteStore,
              [styles.pdpButton]: isModal,
            })}
            disabled
          >
            {t('common:stores.myStore')}
          </button>
        ) : (
          !isModal &&
          !storesData.data.exclude_from_spu && (
            <button
              type="button"
              className={cn(styles.button, {
                [styles.pdpButton]: isModal,
              })}
              onClick={handleSelectMyStore}
            >
              {t('storeFinder.setAsMyStore')}
            </button>
          )
        )}
      </div>

      {isModal && (
        <StoreAvailability
          storeId={storeId}
          isModal
          abVariant={null}
          pickupMessage={pickupMessage}
        />
      )}
      {today?.dow_notice && (
        <p className={styles.notice}>
          {today?.dow_notice ? today?.dow_notice : ''}
        </p>
      )}
      <div>
        <span className={styles.text}>{addressStreet}</span>
        {!isModal && (
          <>
            <span className={styles.text}>
              {addressCity}, {addressProvince}, {addressPostalCode}
            </span>
            <a className={styles.phone} href={`tel: ${phone}`}>
              {phone}
            </a>
          </>
        )}
      </div>
      <div
        className={cn(styles.splitBottom, {
          [styles.pdpSplit]: isModal,
        })}
      >
        <span className={styles.hours}>{todaysHours}</span>
        {isModal && !isFavouriteStore && !storesData.data.exclude_from_spu && (
          <button
            type="button"
            className={cn(styles.button, {
              [styles.pdpButton]: isModal,
            })}
            onClick={handleSelectMyStore}
          >
            {t('common:select.my.store')}
          </button>
        )}
        {!isModal && (
          <a
            href={driveDirections}
            className={styles.directions}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('storeFinder.map.getdirections')}
          </a>
        )}
      </div>
    </address>
  );
};

export default StoreCard;
